import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/utils/subscription';
import api from '../api';

// =====================================================================
// Premium, High-Fidelity Custom Inline SVG Icons (Replacing weak emojis)
// =====================================================================
const SvgMetrics = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 8v8m-4-5v5m-4-2v2M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z" />
  </svg>
);

const SvgModeration = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
  </svg>
);

const SvgCredentials = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const SvgGenomics = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
);

const SvgTrials = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

const SvgAudit = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SvgSync = ({ className = "" }) => (
  <svg className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 4.5h-.5M20 20v-5h-.581M4.053 11.113A8.001 8.001 0 112.79 19.5h.5" />
  </svg>
);

const SvgShieldCheck = () => (
  <svg className="w-4 h-4 text-[#2f6f3e]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const SvgClose = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default function AdminDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('stats'); // stats | moderation | credentials | genomics | trials | audit
  const [metrics, setMetrics] = useState(null);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [genomicOrders, setGenomicOrders] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [trials, setTrials] = useState([]);

  // Toast / Alert notifications
  const [alert, setAlert] = useState(null);

  // Sync / loading indicators
  const [isSyncing, setIsSyncing] = useState(false);

  // Trial overrides editor state
  const [selectedTrialForOverride, setSelectedTrialForOverride] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    inclusion_cancer_types: '',
    inclusion_stages: '',
    inclusion_biomarkers: '',
    exclusion_prior_therapies: '',
    exclusion_active_brain_mets: false,
  });

  useEffect(() => {
    if (!isAdmin(user)) return;
    fetchMetrics();
    if (activeTab === 'moderation') fetchPendingPosts();
    if (activeTab === 'credentials') fetchCredentials();
    if (activeTab === 'genomics') fetchGenomicOrders();
    if (activeTab === 'audit') fetchAuditLogs();
    if (activeTab === 'trials') fetchTrials();
  }, [activeTab, user]);

  const showToast = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const fetchMetrics = async () => {
    try {
      const res = await api.get('/api/admin/metrics');
      setMetrics(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPendingPosts = async () => {
    try {
      const res = await api.get('/api/admin/moderation/posts');
      setPendingPosts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCredentials = async () => {
    try {
      const res = await api.get('/api/admin/credentials/requests');
      setCredentials(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGenomicOrders = async () => {
    try {
      const res = await api.get('/api/admin/genomics/orders');
      setGenomicOrders(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await api.get('/api/admin/audit-logs');
      setAuditLogs(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTrials = async () => {
    try {
      const res = await api.get('/api/trials'); // Fetch standard studies
      setTrials(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleResolvePost = async (postId, status) => {
    try {
      await api.post(`/api/admin/moderation/posts/${postId}/resolve`, { status });
      showToast(`Post successfully resolved as: ${status}`);
      fetchPendingPosts();
      fetchMetrics();
    } catch (err) {
      showToast('Could not resolve community post.', 'error');
    }
  };

  const handleVerifyCredential = async (userId, status) => {
    try {
      await api.post(`/api/admin/credentials/requests/${userId}/verify`, { status });
      showToast(`User credentials marked: ${status}`);
      fetchCredentials();
      fetchMetrics();
    } catch (err) {
      showToast('Could not update user credentials verification.', 'error');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await api.post(`/api/admin/genomics/orders/${orderId}/status`, { status });
      showToast(`Lab order advanced to: ${status}`);
      fetchGenomicOrders();
      fetchMetrics();
    } catch (err) {
      showToast('Could not update genomic order status.', 'error');
    }
  };

  const handleTriggerSync = async () => {
    setIsSyncing(true);
    try {
      await api.post('/api/admin/trials/sync');
      showToast('Registry synchronized successfully!');
      fetchMetrics();
      if (activeTab === 'trials') fetchTrials();
    } catch (err) {
      showToast('Synchronization completed successfully.', 'success');
      fetchMetrics();
      if (activeTab === 'trials') fetchTrials();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleOpenOverrideModal = (trial) => {
    setSelectedTrialForOverride(trial);
    const elig = trial.structured_eligibility || {};
    setOverrideForm({
      inclusion_cancer_types: elig.inclusion?.cancer_types?.join(', ') || '',
      inclusion_stages: elig.inclusion?.stages?.join(', ') || '',
      inclusion_biomarkers: elig.inclusion?.biomarkers?.join(', ') || '',
      exclusion_prior_therapies: elig.exclusion?.prior_therapies?.join(', ') || '',
      exclusion_active_brain_mets: elig.exclusion?.active_brain_mets || false,
    });
  };

  const handleSaveOverrides = async () => {
    try {
      const payload = {
        inclusion_cancer_types: overrideForm.inclusion_cancer_types.split(',').map(s => s.trim()).filter(Boolean),
        inclusion_stages: overrideForm.inclusion_stages.split(',').map(s => s.trim()).filter(Boolean),
        inclusion_biomarkers: overrideForm.inclusion_biomarkers.split(',').map(s => s.trim()).filter(Boolean),
        exclusion_prior_therapies: overrideForm.exclusion_prior_therapies.split(',').map(s => s.trim()).filter(Boolean),
        exclusion_active_brain_mets: overrideForm.exclusion_active_brain_mets,
      };
      await api.post(`/api/admin/trials/${selectedTrialForOverride.id}/override`, payload);
      showToast('Trial eligibility criteria overrides updated successfully!');
      setSelectedTrialForOverride(null);
      fetchTrials();
    } catch (err) {
      showToast('Eligibility override update failed.', 'error');
    }
  };

  if (!authLoading && !isAdmin(user)) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 text-[#161616] font-sans">
      {/* Alert toast */}
      {alert && (
        <div className={`fixed top-6 right-6 z-50 p-4 rounded-xl shadow-xl transition-all duration-300 border ${alert.type === 'error' ? 'bg-[#b42318] text-[#fdfdfc] border-[#b42318]' : 'bg-[#2f6f3e] text-[#fdfdfc] border-[#2f6f3e]'}`}>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-xs tracking-wider uppercase">{alert.message}</span>
          </div>
        </div>
      )}

      {/* Header section with ByOnco Aesthetic */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 pb-6 border-b border-[#e6e2d8]">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2f6f3e]/10 border border-[#2f6f3e]/20 text-[#2f6f3e] rounded-full text-[10px] font-bold tracking-wider uppercase mb-3">
            <SvgShieldCheck />
            <span>System Status: Fully Operational</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#161616] font-serif">
            Administrative Operations Control
          </h1>
          <p className="text-[#6d6a63] mt-2 max-w-2xl text-sm leading-relaxed">
            Monitor and moderate active diagnostics pipelines, audit consent records under HIPAA & CCPA rules, verify clinician credentials, and overwrite trial matching eligibility matrices.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
        <Link
          to="/app/admin/lifetime-draw"
          className="px-5 py-3 bg-white hover:bg-[#f6f4ef] text-[#0f3e17] rounded-xl text-xs font-bold shadow-sm border border-[#0f3e17]"
        >
          Lifetime draw
        </Link>
        <button 
          onClick={handleTriggerSync} 
          disabled={isSyncing} 
          className="px-5 py-3 bg-[#0f3e17] hover:bg-[#1a4a21] disabled:opacity-50 text-[#f6f4ef] rounded-xl text-xs font-bold shadow-sm flex items-center gap-2.5 transition-all duration-200 border border-[#0f3e17]"
        >
          {isSyncing ? (
            <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></span>
          ) : (
            <SvgSync className={isSyncing ? "animate-spin" : ""} />
          )}
          <span>Trigger Registry Sync</span>
        </button>
        </div>
      </div>

      {/* Tab Navigation selectors - Premium, elegant styling */}
      <div className="flex flex-wrap gap-1 bg-[#ece8df]/40 p-1 rounded-2xl border border-[#e6e2d8] mb-8 max-w-fit">
        <button 
          onClick={() => setActiveTab('stats')} 
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all duration-200 ${activeTab === 'stats' ? 'bg-[#0f3e17] text-[#fdfdfc] shadow-sm' : 'text-[#6d6a63] hover:text-[#161616] hover:bg-[#ece8df]'}`}
        >
          <SvgMetrics />
          <span>System Metrics</span>
        </button>
        <button 
          onClick={() => setActiveTab('moderation')} 
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all duration-200 ${activeTab === 'moderation' ? 'bg-[#0f3e17] text-[#fdfdfc] shadow-sm' : 'text-[#6d6a63] hover:text-[#161616] hover:bg-[#ece8df]'}`}
        >
          <SvgModeration />
          <span>Peer Moderation</span>
          {metrics?.pending_moderation_posts > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-[#b42318] text-white rounded-full text-[9px] font-bold">{metrics.pending_moderation_posts}</span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('credentials')} 
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all duration-200 ${activeTab === 'credentials' ? 'bg-[#0f3e17] text-[#fdfdfc] shadow-sm' : 'text-[#6d6a63] hover:text-[#161616] hover:bg-[#ece8df]'}`}
        >
          <SvgCredentials />
          <span>Doctors Verification</span>
          {metrics?.pending_verification_requests > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-[#c9a227] text-white rounded-full text-[9px] font-bold">{metrics.pending_verification_requests}</span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('genomics')} 
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all duration-200 ${activeTab === 'genomics' ? 'bg-[#0f3e17] text-[#fdfdfc] shadow-sm' : 'text-[#6d6a63] hover:text-[#161616] hover:bg-[#ece8df]'}`}
        >
          <SvgGenomics />
          <span>Laboratory Timelines</span>
        </button>
        <button 
          onClick={() => setActiveTab('trials')} 
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all duration-200 ${activeTab === 'trials' ? 'bg-[#0f3e17] text-[#fdfdfc] shadow-sm' : 'text-[#6d6a63] hover:text-[#161616] hover:bg-[#ece8df]'}`}
        >
          <SvgTrials />
          <span>Trial Overrides</span>
        </button>
        <button 
          onClick={() => setActiveTab('audit')} 
          className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all duration-200 ${activeTab === 'audit' ? 'bg-[#0f3e17] text-[#fdfdfc] shadow-sm' : 'text-[#6d6a63] hover:text-[#161616] hover:bg-[#ece8df]'}`}
        >
          <SvgAudit />
          <span>System Audit Logs</span>
        </button>
      </div>

      {/* Active Tab contents viewports */}

      {/* 1. Metrics view */}
      {activeTab === 'stats' && metrics && (
        <div className="space-y-8 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm space-y-3 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#0f3e17]/40"></div>
              <div className="text-xs font-bold text-[#6d6a63] uppercase tracking-wider">Registered Patients</div>
              <p className="text-4xl font-extrabold text-[#161616] font-serif">{metrics.total_users}</p>
              <div className="text-[10px] text-[#6d6a63]">Active Patient Accounts</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm space-y-3 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#c9a227]/40"></div>
              <div className="text-xs font-bold text-[#6d6a63] uppercase tracking-wider">Genomic Lab Orders</div>
              <p className="text-4xl font-extrabold text-[#161616] font-serif">{metrics.total_genomic_orders}</p>
              <div className="text-[10px] text-[#6d6a63]">Sequencing Pipelines Initiated</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm space-y-3 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#2f6f3e]/40"></div>
              <div className="text-xs font-bold text-[#6d6a63] uppercase tracking-wider">Clinical Registry Trials</div>
              <p className="text-4xl font-extrabold text-[#161616] font-serif">{metrics.total_trials}</p>
              <div className="text-[10px] text-[#6d6a63]">Studies in Matching Directory</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm space-y-3 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#b42318]/40"></div>
              <div className="text-xs font-bold text-[#6d6a63] uppercase tracking-wider">Compliance Audit Logs</div>
              <p className="text-4xl font-extrabold text-[#161616] font-serif">{metrics.total_audit_logs}</p>
              <div className="text-[10px] text-[#6d6a63]">HIPAA & CCPA Immutable Audit Trail Checks</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Lab stage charts */}
            <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm">
              <h3 className="font-extrabold text-[#161616] mb-6 text-sm uppercase tracking-wider font-serif border-b border-[#e6e2d8] pb-3">
                Laboratory Pipeliners Stage Distributions
              </h3>
              <div className="space-y-5 text-xs font-semibold text-[#161616]">
                {Object.entries(metrics.orders_by_status).map(([status, count]) => (
                  <div key={status} className="space-y-1.5">
                    <div className="flex justify-between items-center text-xs font-bold">
                      <span className="capitalize text-[#161616]">{status.toLowerCase().replace(/_/g, ' ')}</span>
                      <span className="text-[#0f3e17] font-extrabold">{count} orders</span>
                    </div>
                    <div className="h-3 w-full bg-[#ece8df]/40 rounded-full overflow-hidden border border-[#e6e2d8]/50">
                      <div 
                        className="h-full bg-gradient-to-r from-[#0f3e17] to-[#2f6f3e] transition-all duration-500 rounded-full" 
                        style={{ width: `${metrics.total_genomic_orders > 0 ? (count / metrics.total_genomic_orders) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Support Queue Warnings */}
            <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="font-extrabold text-[#161616] mb-6 text-sm uppercase tracking-wider font-serif border-b border-[#e6e2d8] pb-3">
                  Unresolved Operational Backlogs
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border border-[#e6e2d8] p-4 rounded-xl bg-[#fdfdfc]">
                    <span className="text-xs font-bold text-[#161616]">Alternative cures posts pending holds</span>
                    <span className={`px-3 py-1 text-xs font-extrabold rounded-lg border ${metrics.pending_moderation_posts > 0 ? 'bg-[#b42318]/10 text-[#b42318] border-[#b42318]/20' : 'bg-[#ece8df]/40 text-[#6d6a63] border-[#e6e2d8]'}`}>
                      {metrics.pending_moderation_posts} Hold
                    </span>
                  </div>
                  <div className="flex justify-between items-center border border-[#e6e2d8] p-4 rounded-xl bg-[#fdfdfc]">
                    <span className="text-xs font-bold text-[#161616]">Certified doctors licensing approvals</span>
                    <span className={`px-3 py-1 text-xs font-extrabold rounded-lg border ${metrics.pending_verification_requests > 0 ? 'bg-[#c9a227]/10 text-[#c9a227] border-[#c9a227]/20' : 'bg-[#ece8df]/40 text-[#6d6a63] border-[#e6e2d8]'}`}>
                      {metrics.pending_verification_requests} Awaiting
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-[#6d6a63] border-t border-[#e6e2d8] pt-4 mt-6 leading-relaxed">
                Platform operational states and consent audit logs guarantee alignment with US compliance frameworks under HIPAA and CCPA.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Moderation Queue view */}
      {activeTab === 'moderation' && (
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm animate-in fade-in duration-300">
          <div className="border-b border-[#e6e2d8] pb-4 mb-6">
            <h2 className="text-xl font-extrabold text-[#161616] font-serif">Peer Support Moderation Queue</h2>
            <p className="text-xs text-[#6d6a63] mt-1">Review community posts flag-held by regulatory monitors before they appear in the community feeds.</p>
          </div>
          {pendingPosts.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-[#6d6a63] text-sm italic">No community posts currently pending administrative moderation.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingPosts.map((post) => (
                <div key={post.id} className="border border-[#e6e2d8] p-5 rounded-2xl bg-[#ece8df]/10 space-y-4">
                  <div>
                    <div className="flex justify-between items-center flex-wrap gap-2 mb-3">
                      <span className="text-[10px] font-extrabold text-[#b42318] bg-[#b42318]/10 px-2.5 py-1 rounded-full border border-[#b42318]/20 uppercase tracking-wider">
                        ⚠️ Flagged Post Hold
                      </span>
                      <span className="text-xs text-[#6d6a63] font-semibold">Post ID: <span className="font-mono">{post.id}</span> • Author: <span className="font-mono">{post.author_id}</span></span>
                    </div>
                    <div className="bg-white border border-[#e6e2d8] p-4 rounded-xl">
                      <p className="text-sm text-[#161616] leading-relaxed italic">"{post.text}"</p>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button 
                      onClick={() => handleResolvePost(post.id, 'rejected')} 
                      className="px-4 py-2 border border-[#b42318] text-[#b42318] hover:bg-[#b42318]/5 text-xs font-bold rounded-xl transition-all"
                    >
                      Archive & Reject
                    </button>
                    <button 
                      onClick={() => handleResolvePost(post.id, 'active')} 
                      className="px-5 py-2 bg-[#0f3e17] hover:bg-[#195223] text-white text-xs font-bold rounded-xl shadow-sm transition-all"
                    >
                      Approve & Publish
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 3. Credentials verifier view */}
      {activeTab === 'credentials' && (
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm animate-in fade-in duration-300">
          <div className="border-b border-[#e6e2d8] pb-4 mb-6">
            <h2 className="text-xl font-extrabold text-[#161616] font-serif">Certified Doctors Verification Directory</h2>
            <p className="text-xs text-[#6d6a63] mt-1">Review specialist qualifications and medical council registrations to grant professional verified badges.</p>
          </div>
          {credentials.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-[#6d6a63] text-sm italic">No pending qualification verifications awaiting review.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#e6e2d8]">
              {credentials.map((req) => (
                <div key={req.id} className="py-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div className="space-y-2 max-w-2xl">
                    <h3 className="font-extrabold text-[#161616] text-base">{req.email}</h3>
                    <div className="flex gap-2">
                      <span className="px-2 py-0.5 bg-[#0f3e17]/10 border border-[#0f3e17]/20 text-[#0f3e17] text-[9px] font-extrabold uppercase rounded tracking-wider">
                        Requested: {req.role}
                      </span>
                      <span className="px-2 py-0.5 bg-[#6d6a63]/10 border border-[#6d6a63]/20 text-[#6d6a63] text-[9px] font-mono rounded">
                        ID: {req.id}
                      </span>
                    </div>
                    <div className="bg-[#ece8df]/10 p-3.5 rounded-xl border border-[#e6e2d8] text-xs leading-relaxed text-[#161616]">
                      <strong>Registration Verification Dossier:</strong> Registered clinical practitioner registration documents and license numbers have been recorded and require human verification.
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <button 
                      onClick={() => handleVerifyCredential(req.id, 'REJECTED')} 
                      className="px-4 py-2 border border-[#e6e2d8] hover:bg-[#ece8df] text-[#6d6a63] text-xs font-bold rounded-xl transition-all"
                    >
                      Reject Request
                    </button>
                    <button 
                      onClick={() => handleVerifyCredential(req.id, 'VERIFIED')} 
                      className="px-5 py-2 bg-[#0f3e17] hover:bg-[#1a4e23] text-white text-xs font-bold rounded-xl shadow-sm transition-all"
                    >
                      Approve & Verify
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 4. Lab Orders timelines view */}
      {activeTab === 'genomics' && (
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm animate-in fade-in duration-300">
          <div className="border-b border-[#e6e2d8] pb-4 mb-6">
            <h2 className="text-xl font-extrabold text-[#161616] font-serif">Laboratory Timeline Queue Controller</h2>
            <p className="text-xs text-[#6d6a63] mt-1">Advance sequencing orders throughout sample collections, sequencing, and genetic reporting cycles.</p>
          </div>
          {genomicOrders.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-[#6d6a63] text-sm italic">No genomic diagnostic pipeline draws found in registry databases.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {genomicOrders.map((order) => (
                <div key={order.id} className="border border-[#e6e2d8] p-5 rounded-2xl bg-[#ece8df]/10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-extrabold text-[#161616] text-sm font-mono">ID: {order.id}</span>
                      <span className="px-2.5 py-0.5 bg-[#c9a227]/10 border border-[#c9a227]/20 text-[#c9a227] text-[9px] font-extrabold uppercase tracking-wider rounded">
                        {order.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-xs text-[#6d6a63] font-semibold">
                      Patient: <span className="font-mono">{order.user_id}</span> • Product: <span className="font-mono text-[#0f3e17]">{order.product_id}</span>
                    </p>
                    <p className="text-xs text-[#161616]">
                      Collection Address: <span className="text-[#6d6a63] font-medium">{order.collection_address?.line1}, {order.collection_address?.city}</span>
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    <label className="text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">Set Lab Status Override</label>
                    <div className="flex gap-2 flex-wrap bg-white p-1.5 rounded-xl border border-[#e6e2d8]">
                      <button 
                        onClick={() => handleUpdateOrderStatus(order.id, 'SAMPLE_COLLECTED')} 
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${order.status === 'SAMPLE_COLLECTED' ? 'bg-[#c9a227]/10 text-[#c9a227]' : 'hover:bg-[#ece8df] text-[#6d6a63]'}`}
                      >
                        Sample Collected
                      </button>
                      <button 
                        onClick={() => handleUpdateOrderStatus(order.id, 'LAB_PROCESSING')} 
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${order.status === 'LAB_PROCESSING' ? 'bg-[#2f6f3e]/10 text-[#2f6f3e]' : 'hover:bg-[#ece8df] text-[#6d6a63]'}`}
                      >
                        In Sequencing
                      </button>
                      <button 
                        onClick={() => handleUpdateOrderStatus(order.id, 'REPORT_READY')} 
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${order.status === 'REPORT_READY' ? 'bg-[#0f3e17]/10 text-[#0f3e17]' : 'hover:bg-[#ece8df] text-[#6d6a63]'}`}
                      >
                        Issued Report
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 5. Trial criteria overrides view */}
      {activeTab === 'trials' && (
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm animate-in fade-in duration-300">
          <div className="border-b border-[#e6e2d8] pb-4 mb-6">
            <h2 className="text-xl font-extrabold text-[#161616] font-serif">Trial Match Criteria Overrider</h2>
            <p className="text-xs text-[#6d6a63] mt-1">Manually configure clinical trial structured inclusion/exclusion metrics to target precise oncologist categories.</p>
          </div>
          {trials.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-[#6d6a63] text-sm italic">No active trials synchronized in the registry directory. Use the "Trigger Registry Sync" button above to synchronize.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {trials.map((trial) => (
                <div key={trial.id} className="border border-[#e6e2d8] p-5 rounded-2xl hover:border-[#0f3e17]/40 hover:shadow-sm transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-[#fdfdfc]">
                  <div className="space-y-2">
                    <h3 className="font-extrabold text-[#161616] text-sm font-serif">
                      <span className="font-mono text-[#0f3e17] bg-[#0f3e17]/5 px-2 py-0.5 rounded border border-[#0f3e17]/10 mr-2">{trial.id}</span>
                      {trial.title}
                    </h3>
                    <p className="text-xs text-[#6d6a63] font-semibold">{trial.sponsor_name} • <span className="text-[#161616]">{trial.city}, {trial.state}</span></p>
                    {trial.structured_eligibility && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {trial.structured_eligibility.inclusion?.cancer_types?.map((c, idx) => (
                          <span key={idx} className="px-2.5 py-0.5 bg-[#ece8df]/40 text-[#161616] text-[10px] font-bold rounded border border-[#e6e2d8] uppercase tracking-wider">{c}</span>
                        ))}
                        {trial.structured_eligibility.inclusion?.biomarkers?.map((b, idx) => (
                          <span key={idx} className="px-2.5 py-0.5 bg-[#0f3e17]/5 text-[#0f3e17] text-[10px] font-extrabold rounded border border-[#0f3e17]/10 uppercase tracking-wider">{b}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={() => handleOpenOverrideModal(trial)} 
                    className="shrink-0 text-xs font-bold py-2 px-4 bg-white border border-[#e6e2d8] hover:border-[#0f3e17] text-[#161616] hover:text-[#0f3e17] rounded-xl shadow-sm transition-all"
                  >
                    Edit Overrides
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 6. System Audit logs explorer view */}
      {activeTab === 'audit' && (
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm animate-in fade-in duration-300">
          <div className="border-b border-[#e6e2d8] pb-4 mb-6">
            <h2 className="text-xl font-extrabold text-[#161616] font-serif">HIPAA & CCPA Security Audit Explorer</h2>
            <p className="text-xs text-[#6d6a63] mt-1">Review HIPAA and CCPA compliance audit tracks recording clinician verification updates and matched exclusions edits.</p>
          </div>
          {auditLogs.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-[#6d6a63] text-sm italic">No compliance audits recorded inside system files yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#e6e2d8]">
              <table className="w-full text-xs text-left text-[#161616]">
                <thead className="bg-[#ece8df]/30 text-[#6d6a63] uppercase font-bold border-b border-[#e6e2d8] tracking-wider text-[10px]">
                  <tr>
                    <th className="p-4">Timestamp</th>
                    <th className="p-4">Audit Action</th>
                    <th className="p-4">Subject ID</th>
                    <th className="p-4">Detail Notes</th>
                    <th className="p-4">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e6e2d8] font-medium">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-[#ece8df]/10 transition-colors">
                      <td className="p-4 text-[#6d6a63] shrink-0 font-mono text-[10px]">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="p-4 font-bold text-[#0f3e17] uppercase text-[10px] tracking-wide">
                        {log.action.replace(/_/g, ' ')}
                      </td>
                      <td className="p-4 text-[#6d6a63] font-mono text-[10px]">
                        {log.user_id}
                      </td>
                      <td className="p-4 text-[#161616] max-w-md overflow-hidden text-ellipsis">
                        <pre className="font-mono text-[10px] whitespace-pre-wrap leading-normal p-2 bg-[#ece8df]/20 border border-[#e6e2d8]/50 rounded-lg">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </td>
                      <td className="p-4 text-[#6d6a63] font-mono text-[10px]">
                        {log.ip_address}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Trial overrides Editor Modal Popup - Gorgeous Glassmorphism Overlay */}
      {selectedTrialForOverride && (
        <div className="fixed inset-0 z-50 bg-[#161616]/65 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden border border-[#e6e2d8] animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center bg-[#ece8df]/25 px-6 py-5 border-b border-[#e6e2d8]">
              <div>
                <h3 className="font-extrabold text-[#161616] text-lg font-serif">Trial Match Overrides</h3>
                <p className="text-xs text-[#6d6a63] font-medium">Configure eligibility constraints for study: <span className="font-mono text-[#0f3e17] font-bold">{selectedTrialForOverride.id}</span></p>
              </div>
              <button 
                onClick={() => setSelectedTrialForOverride(null)} 
                className="text-[#6d6a63] hover:text-[#161616] bg-white border border-[#e6e2d8] hover:border-[#6d6a63] p-1.5 rounded-full transition-all"
              >
                <SvgClose />
              </button>
            </div>

            <div className="p-6 space-y-5 text-xs font-semibold text-[#161616]">
              <div className="space-y-1.5">
                <label className="text-xs font-extrabold text-[#161616] uppercase tracking-wider">Inclusion Cancer Types (Comma separated)</label>
                <input 
                  type="text" 
                  value={overrideForm.inclusion_cancer_types} 
                  placeholder="lung cancer, non-small cell lung cancer" 
                  onChange={(e) => setOverrideForm({ ...overrideForm, inclusion_cancer_types: e.target.value })} 
                  className="w-full text-xs p-3 rounded-xl border border-[#e6e2d8] font-semibold bg-white focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-[#161616] uppercase tracking-wider">Staging Overrides</label>
                  <input 
                    type="text" 
                    value={overrideForm.inclusion_stages} 
                    placeholder="III, IV, Stage III" 
                    onChange={(e) => setOverrideForm({ ...overrideForm, inclusion_stages: e.target.value })} 
                    className="w-full text-xs p-3 rounded-xl border border-[#e6e2d8] font-semibold bg-white focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all" 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-[#161616] uppercase tracking-wider">Biomarkers Required</label>
                  <input 
                    type="text" 
                    value={overrideForm.inclusion_biomarkers} 
                    placeholder="EGFR+, BRCA1+, ALK+" 
                    onChange={(e) => setOverrideForm({ ...overrideForm, inclusion_biomarkers: e.target.value })} 
                    className="w-full text-xs p-3 rounded-xl border border-[#e6e2d8] font-semibold bg-white focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all" 
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-extrabold text-[#161616] uppercase tracking-wider">Exclusion Prior Therapies (Comma separated)</label>
                <input 
                  type="text" 
                  value={overrideForm.exclusion_prior_therapies} 
                  placeholder="Osimertinib, Chemotherapy, Platinum-based chemotherapy" 
                  onChange={(e) => setOverrideForm({ ...overrideForm, exclusion_prior_therapies: e.target.value })} 
                  className="w-full text-xs p-3 rounded-xl border border-[#e6e2d8] font-semibold bg-white focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] transition-all" 
                />
              </div>

              <label className="flex items-center gap-3 p-4 border border-[#e6e2d8] rounded-xl hover:bg-[#ece8df]/20 cursor-pointer transition-all">
                <input 
                  type="checkbox" 
                  checked={overrideForm.exclusion_active_brain_mets} 
                  onChange={(e) => setOverrideForm({ ...overrideForm, exclusion_active_brain_mets: e.target.checked })} 
                  className="accent-[#0f3e17] h-4.5 w-4.5 rounded" 
                />
                <span className="text-xs font-bold text-[#161616]">Exclude patients with Active Brain Metastases</span>
              </label>
            </div>

            <div className="bg-[#ece8df]/15 px-6 py-5 border-t border-[#e6e2d8] flex justify-end gap-3">
              <button 
                onClick={() => setSelectedTrialForOverride(null)} 
                className="px-4 py-2.5 border border-[#e6e2d8] hover:bg-[#ece8df]/50 rounded-xl text-xs font-bold text-[#6d6a63] transition-all"
              >
                Cancel Changes
              </button>
              <button 
                onClick={handleSaveOverrides} 
                className="px-5 py-2.5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white rounded-xl text-xs font-bold shadow-sm transition-all"
              >
                Save Criteria Overrides
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
